import { Component, inject, signal, computed, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';
import { TranslatePipe } from './i18n.service';
import { addDateKeyDays, aucklandClockParts, aucklandDateKey, dateKeyLabel } from './auckland-time';

@Component({
  selector: 'app-schedule',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslatePipe],
  template: `
    <div class="pb-24 lg:pb-8 flex flex-col min-h-screen lg:min-h-[calc(100vh-72px)]" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex justify-between items-center lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <div>
          <h1 class="text-lg font-semibold tracking-tight">{{ 'schedule' | t }}</h1>
          <p class="text-xs text-white/55 font-medium">{{ monthLabel() }}</p>
        </div>
        <a routerLink="/create-job"
           class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15">
          <i class="ti ti-plus text-[21px]"></i>
        </a>
      </header>

      <section class="bg-white border-b border-slate-100 lg:mx-7 lg:mt-6 lg:rounded-lg lg:border lg:shadow-sm">
        <div class="flex gap-2 px-4 py-3 overflow-x-auto hide-scrollbar">
          @for (day of week(); track day.iso) {
            <button (click)="selectDay(day.iso)"
              class="flex flex-col items-center justify-center w-[50px] h-[64px] rounded-2xl shrink-0 transition-all border"
              [class.bg-[#102C41]]="day.iso === selectedDay()"
              [class.text-white]="day.iso === selectedDay()"
              [class.border-[#102C41]]="day.iso === selectedDay()"
              [class.bg-sky-50]="day.isToday && day.iso !== selectedDay()"
              [class.border-sky-100]="day.isToday && day.iso !== selectedDay()"
              [class.bg-white]="day.iso !== selectedDay() && !day.isToday"
              [class.border-slate-100]="day.iso !== selectedDay() && !day.isToday"
              [class.text-slate-600]="day.iso !== selectedDay()">
              <span class="text-[10px] font-medium uppercase">{{ day.dayName }}</span>
              <span class="text-lg font-semibold mt-0.5">{{ day.dayNum }}</span>
              @if (jobCountForDay(day.iso) > 0) {
                <span class="w-1.5 h-1.5 rounded-full mt-1"
                      [class.bg-white]="day.iso === selectedDay()"
                      [class.bg-[#1A6FA6]]="day.iso !== selectedDay()"></span>
              } @else {
                <span class="w-1.5 h-1.5 mt-1"></span>
              }
            </button>
          }
        </div>

        <div class="px-5 pb-3 flex items-center justify-between">
          <span class="text-xs font-medium text-slate-500">{{ selectedDayLabel() }}</span>
          <a routerLink="/work-list/jobs-assigned" class="text-xs font-semibold text-[#1A6FA6]">{{ 'viewAll' | t }}</a>
        </div>
      </section>

      <main class="px-4 pt-5 flex-1 space-y-5 lg:px-7 lg:pt-5">
        @if (loadError()) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Unable to load schedule</p>
              <p class="text-xs text-rose-600 mt-0.5">{{ loadError() }}</p>
            </div>
            <button (click)="loadJobs()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">
              {{ 'retry' | t }}
            </button>
          </section>
        }

        @if (isLoading()) {
          <section class="space-y-3">
            @for (n of [1,2,3]; track n) {
              <div class="bg-white rounded-2xl p-4 flex gap-3 items-center animate-pulse border border-slate-100">
                <div class="w-12 h-12 rounded-xl bg-slate-100 shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 bg-slate-100 rounded-full w-1/3"></div>
                  <div class="h-4 bg-slate-100 rounded-full w-2/3"></div>
                  <div class="h-3 bg-slate-100 rounded-full w-1/2"></div>
                </div>
              </div>
            }
          </section>
        } @else {
          @if (activeJobs().length) {
            <section class="rounded-2xl border border-emerald-100 bg-white overflow-hidden shadow-sm">
              <div class="w-full h-11 px-4 bg-white flex items-center justify-between">
                <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>In Progress <span class="text-xs text-slate-400">{{ activeJobs().length }}</span></span>
                <i class="ti ti-chevron-right text-slate-300 rotate-90"></i>
              </div>
              <div class="px-3 pb-3 space-y-2 bg-emerald-50/40">
                @for (job of activeJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]"
                     class="block rounded-xl p-4 text-white active:scale-[0.98] transition-all"
                     style="background:linear-gradient(135deg,#0F334A,#145B7A); border:1px solid rgba(255,255,255,0.12);">
                    <div class="flex items-start justify-between gap-3 mb-2">
                      <div class="min-w-0">
                        <div class="flex items-center gap-2 mb-1.5">
                          <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase bg-white/10 border border-white/15 text-white/80">{{ job.job_type }}</span>
                          <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase bg-emerald-400/15 border border-emerald-200/25 text-emerald-100">Live</span>
                        </div>
                        <h3 class="font-semibold text-base truncate">{{ job.customer }}</h3>
                        <p class="text-[11px] text-white/55 uppercase tracking-wider">{{ job.job_id }}</p>
                      </div>
                      <div class="text-right shrink-0">
                        <p class="text-[10px] text-white/55 uppercase tracking-wider">Elapsed</p>
                        <p class="font-mono text-lg font-semibold">{{ activeTimer(job) }}</p>
                      </div>
                    </div>
                    <p class="text-xs text-white/70 truncate">
                      <i class="ti ti-map-pin"></i> {{ job.address }}
                    </p>
                  </a>
                }
              </div>
            </section>
          }

          @if (pausedJobs().length) {
            <section class="rounded-2xl border border-orange-100 bg-white overflow-hidden shadow-sm">
              <div class="w-full h-11 px-4 bg-white flex items-center justify-between">
                <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>Paused <span class="text-xs text-slate-400">{{ pausedJobs().length }}</span></span>
                <i class="ti ti-chevron-right text-slate-300 rotate-90"></i>
              </div>
              <div class="px-3 pb-3 space-y-2 bg-orange-50/40">
                @for (job of pausedJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]"
                     class="bg-white rounded-xl p-3 flex gap-3 items-center active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                      <i class="ti ti-player-pause-filled text-xl"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5">
                        <span class="text-[10px] font-semibold text-slate-500">{{ job.job_id }}</span>
                        <span class="text-[9px] font-medium uppercase text-orange-700">Paused</span>
                      </div>
                      <h4 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h4>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ pausedLabel(job) }}</p>
                    </div>
                    <i class="ti ti-chevron-right text-slate-300 shrink-0"></i>
                  </a>
                }
              </div>
            </section>
          }

          <section class="rounded-2xl lg:rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div class="w-full h-11 px-4 bg-white flex items-center justify-between">
                <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>{{ 'scheduledJobs' | t }} <span class="text-xs text-slate-400">{{ selectedDayJobs().length }}</span></span>
              <a routerLink="/work-list/jobs-assigned" class="text-xs font-semibold text-[#1A6FA6]">{{ 'viewAll' | t }}</a>
            </div>
            <div class="px-3 pb-3 space-y-2 bg-slate-50/60">
            @if (selectedDayJobs().length === 0) {
              <div class="flex flex-col items-center justify-center py-14 text-center bg-white rounded-xl">
                <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-4">
                  <i class="ti ti-calendar-off text-2xl text-slate-300"></i>
                </div>
                <h3 class="font-semibold text-slate-700 text-base mb-1">{{ 'noJobsScheduled' | t }}</h3>
                <p class="text-slate-400 text-sm max-w-xs leading-relaxed">{{ 'noAppointmentsAssigned' | t }}</p>
              </div>
            } @else {
              <div class="space-y-2 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
                @for (job of selectedDayJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]"
                     class="bg-white rounded-xl p-3 flex gap-3 items-center active:scale-[0.98] transition-all">
                    <div class="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-sky-50 text-[#1A6FA6] shrink-0 border border-sky-100">
                      <span class="text-[9px] font-semibold uppercase">{{ job.timeParts[1] }}</span>
                      <span class="text-sm font-semibold">{{ job.timeParts[0] }}</span>
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span class="text-[10px] font-semibold text-[#1A6FA6]">{{ job.job_id }}</span>
                        <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                              [class.bg-slate-50]="job.status === 'Scheduled' || job.status === 'Unassigned'"
                              [class.text-slate-500]="job.status === 'Scheduled' || job.status === 'Unassigned'"
                              [class.border-slate-100]="job.status === 'Scheduled' || job.status === 'Unassigned'"
                              [class.bg-amber-50]="job.status === 'Quote Sent' || job.status === 'Approved'"
                              [class.text-amber-700]="job.status === 'Quote Sent' || job.status === 'Approved'"
                              [class.border-amber-100]="job.status === 'Quote Sent' || job.status === 'Approved'"
                              [class.bg-sky-50]="job.status === 'Ready for Office'"
                              [class.text-sky-700]="job.status === 'Ready for Office'"
                              [class.border-sky-100]="job.status === 'Ready for Office'"
                              [class.bg-emerald-50]="job.status === 'Completed'"
                              [class.text-emerald-700]="job.status === 'Completed'"
                              [class.border-emerald-100]="job.status === 'Completed'">
                          {{ job.status }}
                        </span>
                        @if (job.job_type && job.job_type !== 'Service') {
                          <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                                [class.bg-sky-50]="job.job_type === 'Inspection'"
                                [class.text-sky-700]="job.job_type === 'Inspection'"
                                [class.border-sky-100]="job.job_type === 'Inspection'"
                                [class.bg-rose-50]="job.job_type === 'Emergency'"
                                [class.text-rose-700]="job.job_type === 'Emergency'"
                                [class.border-rose-100]="job.job_type === 'Emergency'">
                            {{ job.job_type }}
                          </span>
                        }
                      </div>
                      <h4 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h4>
                      <p class="text-xs text-slate-500 truncate mt-0.5">
                        <i class="ti ti-map-pin"></i> {{ job.address }}
                      </p>
                    </div>

                    <i class="ti ti-chevron-right text-slate-300 shrink-0"></i>
                  </a>
                }
              </div>
            }
            </div>
          </section>

          @if (unscheduledJobs().length) {
            <section class="rounded-2xl lg:rounded-lg border border-amber-100 bg-white overflow-hidden shadow-sm">
              <div class="w-full h-11 px-4 bg-white flex items-center justify-between">
                <span class="text-sm font-bold text-slate-800 flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{{ 'unscheduledJobs' | t }} <span class="text-xs text-slate-400">{{ unscheduledJobs().length }}</span></span>
                <i class="ti ti-chevron-right text-slate-300 rotate-90"></i>
              </div>
              <div class="px-3 pb-3 space-y-2 bg-amber-50/40">
                @for (job of unscheduledJobs(); track job.job_id) {
                  <a [routerLink]="['/job', job.job_id]"
                     class="bg-white rounded-xl p-3 flex gap-3 items-center active:scale-[0.98] transition-all">
                    <div class="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                      <i class="ti ti-calendar-question text-xl"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5">
                        <span class="text-[10px] font-semibold text-slate-500">{{ job.job_id }}</span>
                        <span class="text-[9px] font-medium uppercase text-amber-700">{{ unscheduledBadge(job) }}</span>
                      </div>
                      <h4 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h4>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ job.job_type }} needs scheduling</p>
                    </div>
                    <i class="ti ti-chevron-right text-slate-300 shrink-0"></i>
                  </a>
                }
              </div>
            </section>
          }
        }
      </main>
    </div>
  `
})
export class ScheduleComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);

  isLoading = signal(true);
  loadError = signal('');
  allJobs = signal<any[]>([]);
  selectedDay = signal(aucklandDateKey());
  tick = signal(0);

  private timerId: ReturnType<typeof setInterval> | undefined;

  week = computed(() => {
    const days = [];
    const today = aucklandDateKey();
    for (let i = -3; i <= 3; i++) {
      const dateKey = addDateKeyDays(today, i);
      days.push({
        iso: dateKey,
        dayName: dateKeyLabel(dateKey, { weekday: 'short' }),
        dayNum: String(Number(dateKey.slice(8, 10))),
        isToday: i === 0,
      });
    }
    return days;
  });

  monthLabel = computed(() => {
    return dateKeyLabel(this.selectedDay(), { month: 'long', year: 'numeric' });
  });

  selectedDayLabel = computed(() => {
    const today = aucklandDateKey();
    if (this.selectedDay() === today) return 'Today';
    return dateKeyLabel(this.selectedDay(), { weekday: 'long', day: 'numeric', month: 'long' });
  });

  activeJobs = computed(() => this.allJobs().filter(j => j.status === 'In Progress'));
  pausedJobs = computed(() => this.allJobs().filter(j => j.status === 'Paused'));
  selectedDayJobs = computed(() => {
    const sel = this.selectedDay();
    return this.allJobs().filter(j => j.scheduledDay === sel && ['Scheduled', 'Approved'].includes(j.status));
  });
  unscheduledJobs = computed(() => {
    const actionable = ['Unassigned', 'Scheduled', 'Approved'];
    return this.allJobs().filter(j => !j.scheduledDay && actionable.includes(j.status));
  });

  ngOnInit() {
    this.loadJobs();
    this.timerId = setInterval(() => this.tick.update(v => v + 1), 1000);
  }

  ngOnDestroy() {
    if (this.timerId) clearInterval(this.timerId);
  }

  loadJobs() {
    this.isLoading.set(true);
    this.loadError.set('');
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.jobs) {
          this.allJobs.set(res.data.jobs.map((j: any) => this.mapJob(j)));
        } else {
          this.loadError.set(res.message || 'Schedule could not be loaded.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.loadError.set(err?.error?.message || 'Schedule could not be loaded.');
      },
    });
  }

  selectDay(iso: string) {
    this.selectedDay.set(iso);
  }

  jobCountForDay(iso: string): number {
    return this.allJobs().filter(j => j.scheduledDay === iso && !['In Progress', 'Paused'].includes(j.status)).length;
  }

  activeTimer(job: any): string {
    this.tick();
    if (!job.startedAt) return '00:00:00';
    const seconds = Math.max(0, Math.floor((Date.now() - job.startedAt.getTime()) / 1000));
    return this.formatDuration(seconds);
  }

  private mapJob(j: any) {
    const schedDate = j.scheduled_date ? new Date(j.scheduled_date) : null;
    const startedAt = j.started_at ? new Date(j.started_at) : null;
    let timeParts = ['--', ''];
    let scheduledDay = '';

    if (schedDate && !Number.isNaN(schedDate.getTime())) {
      scheduledDay = aucklandDateKey(schedDate);
      timeParts = aucklandClockParts(schedDate);
    }

    return {
      job_id: j.id,
      customer: j.contacts?.find((c: any) => c.contact_type === 'Customer' || c.type === 'Customer')?.name
        || j.customer_name || 'Unknown',
      address: j.address || 'Unknown Address',
      status: j.status,
      job_type: j.type || j.job_type || 'Service',
      timeParts,
      scheduledDay,
      pausedAt: j.pause_started_at || j.paused_at || null,
      startedAt: startedAt && !Number.isNaN(startedAt.getTime()) ? startedAt : null,
    };
  }

  pausedLabel(job: any): string {
    if (!job?.pausedAt) return 'Work is temporarily paused';
    const date = new Date(job.pausedAt);
    if (Number.isNaN(date.getTime())) return 'Work is temporarily paused';
    return `Paused since ${date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', timeZone: 'Pacific/Auckland' })}`;
  }

  unscheduledBadge(job: any): string {
    return job?.status === 'Scheduled' ? 'Needs scheduling' : job?.status || 'Unscheduled';
  }

  private formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

}
